import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Mail, MapPin, Phone } from 'lucide-react';
import { API } from '../lib/api';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            await axios.post(`${API}/contact`, formData);
            setStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            setStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="contact-page">
            <SEO
                title="Contact Us"
                description="Get in touch with Wayside Maple. Wholesale inquiries, custom orders, or questions about our maple syrup products — we'd love to hear from you. Located in Troy, Pennsylvania."
                keywords="contact Wayside Maple, maple syrup wholesale, custom orders, Troy PA, customer service"
                path="/contact"
            />
            <Header />
            
            {/* Hero */}
            <section className="pt-32 pb-16 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <span className="text-maple-red font-mono text-sm tracking-widest">
                        GET IN TOUCH
                    </span>
                    <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl text-maple-dark mt-4">
                        CONTACT US
                    </h1>
                    <p className="text-maple-dark/60 text-lg mt-6 max-w-2xl">
                        Have questions about our products or want to place a wholesale order? 
                        We'd love to hear from you.
                    </p>
                </div>
            </section>

            {/* Contact Content */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div>
                            <h2 className="font-anton text-3xl text-maple-dark mb-8">
                                GET IN TOUCH
                            </h2>
                            
                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-maple-secondary flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-5 h-5 text-maple-red" />
                                    </div>
                                    <div>
                                        <h3 className="font-anton text-lg text-maple-dark">Location</h3>
                                        <p className="text-maple-dark/60 mt-1">Troy, Pennsylvania<br />United States</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-maple-secondary flex items-center justify-center flex-shrink-0">
                                        <Mail className="w-5 h-5 text-maple-red" />
                                    </div>
                                    <div>
                                        <h3 className="font-anton text-lg text-maple-dark">Email</h3>
                                        <a href="mailto:info@waysidemaplefarm.com" className="text-maple-dark/60 hover:text-maple-red transition-colors mt-1 block">
                                            info@waysidemaplefarm.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-maple-secondary flex items-center justify-center flex-shrink-0">
                                        <Phone className="w-5 h-5 text-maple-red" />
                                    </div>
                                    <div>
                                        <h3 className="font-anton text-lg text-maple-dark">Phone</h3>
                                        <p className="text-maple-dark/60 mt-1">Available upon request</p>
                                    </div>
                                </div>
                            </div>

                            {/* Map placeholder */}
                            <div className="mt-12 aspect-video bg-maple-secondary border border-black/10 flex items-center justify-center">
                                <span className="text-maple-dark/40 font-mono text-sm">Troy, PA</span>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-maple-secondary border border-black/10 p-8 md:p-12">
                            <h2 className="font-anton text-3xl text-maple-dark mb-8">
                                SEND A MESSAGE
                            </h2>

                            {status.message && (
                                <div className={`mb-6 p-4 ${
                                    status.type === 'success' 
                                        ? 'bg-green-100 border border-green-300 text-green-700' 
                                        : 'bg-red-100 border border-red-300 text-red-700'
                                }`} data-testid="contact-status">
                                    {status.message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                <div>
                                    <label className="block text-maple-dark/40 text-sm uppercase tracking-wider mb-2">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-white"
                                        placeholder="Your name"
                                        data-testid="contact-name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-maple-dark/40 text-sm uppercase tracking-wider mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-white"
                                        placeholder="your@email.com"
                                        data-testid="contact-email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-maple-dark/40 text-sm uppercase tracking-wider mb-2">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-white"
                                        placeholder="What's this about?"
                                        data-testid="contact-subject"
                                    />
                                </div>

                                <div>
                                    <label className="block text-maple-dark/40 text-sm uppercase tracking-wider mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows={5}
                                        className="w-full resize-none bg-white"
                                        placeholder="Your message..."
                                        data-testid="contact-message"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="contact-submit"
                                >
                                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactPage;